import React from 'react';
import { motion } from 'framer-motion';
import { Thermometer, Droplets, Scale, Activity, Wifi, WifiOff } from 'lucide-react';

interface Device {
  id: string;
  name: string;
  type: 'oven' | 'fridge' | 'scale' | 'thermometer';
  status: 'online' | 'offline';
  lastUpdate: number;
  sensors: {
    temperature?: number;
    humidity?: number;
    pH?: number;
    weight?: number;
  };
}

interface DeviceCardProps {
  device: Device;
}

const DeviceCard: React.FC<DeviceCardProps> = ({ device }) => {
  const getDeviceIcon = () => {
    switch (device.type) {
      case 'oven':
        return <Thermometer className="w-8 h-8" />;
      case 'fridge':
        return <Droplets className="w-8 h-8" />;
      case 'scale':
        return <Scale className="w-8 h-8" />;
      case 'thermometer':
        return <Activity className="w-8 h-8" />;
      default:
        return <Activity className="w-8 h-8" />;
    }
  };

  const getDeviceColor = () => {
    switch (device.type) {
      case 'oven':
        return 'from-red-500 to-orange-500';
      case 'fridge':
        return 'from-blue-500 to-cyan-500';
      case 'scale':
        return 'from-green-500 to-teal-500';
      case 'thermometer':
        return 'from-purple-500 to-pink-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const formatLastUpdate = () => {
    const now = Date.now();
    const diff = now - device.lastUpdate;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getDeviceColor()} flex items-center justify-center text-white`}>
          {getDeviceIcon()}
        </div>
        <div className="flex items-center space-x-2">
          {device.status === 'online' ? (
            <Wifi className="w-4 h-4 text-green-400" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-400" />
          )}
          <span className={`text-xs px-2 py-1 rounded-full ${
            device.status === 'online' 
              ? 'bg-green-500/20 text-green-300' 
              : 'bg-red-500/20 text-red-300'
          }`}>
            {device.status}
          </span>
        </div>
      </div>

      <h3 className="text-white font-semibold text-lg mb-2">{device.name}</h3>
      <p className="text-purple-300 text-sm mb-4 capitalize">{device.type}</p>

      <div className="space-y-2 mb-4">
        {Object.entries(device.sensors).map(([key, value]) => (
          <div key={key} className="flex justify-between items-center">
            <span className="text-purple-300 text-sm capitalize">{key}:</span>
            <span className="text-white font-medium">
              {value}
              {key === 'temperature' ? '°C' : 
               key === 'humidity' ? '%' : 
               key === 'weight' ? 'g' : 
               key === 'pH' ? '' : ''}
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center text-xs text-purple-300">
        <span>Last update:</span>
        <span>{formatLastUpdate()}</span>
      </div>

      {/* Status indicator */}
      <div className="mt-4 flex items-center space-x-2">
        <div className={`w-2 h-2 rounded-full ${
          device.status === 'online' ? 'bg-green-400 animate-pulse' : 'bg-red-400'
        }`} />
        <span className="text-xs text-purple-300">
          {device.status === 'online' ? 'Receiving data' : 'Connection lost'}
        </span>
      </div>
    </motion.div>
  );
};

export default DeviceCard;